/**
 * ExternalSystemDialog (P3-C) — Add or edit external system.
 *
 * Type-specific config fields. Slug auto-suggest từ name.
 */
import { Database, Github, Globe, X } from "lucide-react";
import * as React from "react";

import {
  type ExternalSystem,
  externalSystemsApi,
  type SystemType,
} from "@/api/externalSystems";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { useUIStore } from "@/stores/ui";

interface Props {
  open: boolean;
  initial: ExternalSystem | null;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}

const TYPE_OPTIONS: Array<{
  value: SystemType;
  label: string;
  Icon: typeof Github;
  hint: string;
}> = [
  {
    value: "git",
    label: "Git repository",
    Icon: Github,
    hint: "Clone repo + đọc README + dependencies",
  },
  {
    value: "url",
    label: "URL / Web page",
    Icon: Globe,
    hint: "Fetch nội dung trang web",
  },
  {
    value: "database",
    label: "Database",
    Icon: Database,
    hint: "Postgres / MySQL / MSSQL — schema reflection",
  },
];

function slugify(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/đ/g, "d")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
}

export function ExternalSystemDialog({
  open,
  initial,
  onClose,
  onSaved,
}: Props): JSX.Element | null {
  const isEdit = initial !== null;
  const pushToast = useUIStore((s) => s.pushToast);

  const [type, setType] = React.useState<SystemType>(initial?.type ?? "git");
  const [slug, setSlug] = React.useState(initial?.slug ?? "");
  const [slugTouched, setSlugTouched] = React.useState(false);
  const [name, setName] = React.useState(initial?.name ?? "");
  const [description, setDescription] = React.useState(
    initial?.description ?? "",
  );
  const [credentialsSecretName, setCredentialsSecretName] = React.useState(
    initial?.credentials_secret_name ?? "",
  );
  const [busy, setBusy] = React.useState(false);

  // Type-specific config
  const [gitUrl, setGitUrl] = React.useState(
    String(initial?.config?.url ?? ""),
  );
  const [gitBranch, setGitBranch] = React.useState(
    String(initial?.config?.branch ?? ""),
  );
  const [urlValue, setUrlValue] = React.useState(
    String(initial?.config?.url ?? ""),
  );
  const [dbDsn, setDbDsn] = React.useState(
    String(initial?.config?.dsn ?? ""),
  );

  // Reset state when dialog open/initial change
  React.useEffect(() => {
    if (open) {
      setType(initial?.type ?? "git");
      setSlug(initial?.slug ?? "");
      setSlugTouched(false);
      setName(initial?.name ?? "");
      setDescription(initial?.description ?? "");
      setCredentialsSecretName(initial?.credentials_secret_name ?? "");
      setGitUrl(String(initial?.config?.url ?? ""));
      setGitBranch(String(initial?.config?.branch ?? ""));
      setUrlValue(String(initial?.config?.url ?? ""));
      setDbDsn(String(initial?.config?.dsn ?? ""));
    }
  }, [open, initial]);

  // Auto-slug from name
  React.useEffect(() => {
    if (!isEdit && !slugTouched) {
      setSlug(slugify(name));
    }
  }, [name, slugTouched, isEdit]);

  if (!open) return null;

  const buildConfig = (): Record<string, unknown> => {
    if (type === "git") return { url: gitUrl, branch: gitBranch || undefined };
    if (type === "url") return { url: urlValue };
    if (type === "database") return { dsn: dbDsn };
    return {};
  };

  const validate = (): string | null => {
    if (!name.trim()) return "Vui lòng nhập tên";
    if (!slug.trim()) return "Vui lòng nhập slug";
    if (type === "git" && !gitUrl.trim()) return "Git URL bắt buộc";
    if (type === "url" && !urlValue.trim()) return "URL bắt buộc";
    if (type === "database" && !dbDsn.trim()) return "DSN bắt buộc";
    return null;
  };

  const handleSave = async (): Promise<void> => {
    const err = validate();
    if (err) {
      pushToast({ variant: "warning", title: err });
      return;
    }
    setBusy(true);
    try {
      if (isEdit && initial) {
        await externalSystemsApi.update(initial.id, {
          name: name.trim(),
          description: description.trim() || null,
          config: buildConfig(),
          credentials_secret_name: credentialsSecretName.trim() || null,
        });
        pushToast({ variant: "success", title: "Đã cập nhật" });
      } else {
        await externalSystemsApi.create({
          type,
          slug: slug.trim(),
          name: name.trim(),
          description: description.trim() || null,
          config: buildConfig(),
          credentials_secret_name: credentialsSecretName.trim() || null,
        });
        pushToast({ variant: "success", title: "Đã thêm system" });
      }
      await onSaved();
      onClose();
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Lưu thất bại",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-xl border bg-popover shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between border-b px-4 py-3 sticky top-0 bg-popover z-10">
          <h2 className="text-sm font-semibold">
            {isEdit ? "Sửa external system" : "Thêm external system"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
          >
            <X className="size-4" />
          </button>
        </header>

        <div className="p-4 space-y-4">
          {/* Type selector — only when creating */}
          {!isEdit ? (
            <div>
              <label className="text-xs font-medium block mb-1.5">Loại</label>
              <div className="grid grid-cols-3 gap-2">
                {TYPE_OPTIONS.map((opt) => {
                  const Icon = opt.Icon;
                  const selected = type === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setType(opt.value)}
                      className={cn(
                        "rounded-md border p-2.5 text-left transition-all",
                        selected
                          ? "border-primary ring-1 ring-primary/30 bg-gradient-brand-soft"
                          : "hover:border-primary/50",
                      )}
                    >
                      <Icon className="size-4 mb-1" />
                      <div className="text-xs font-medium">{opt.label}</div>
                      <div className="text-[10px] text-muted-foreground mt-0.5">
                        {opt.hint}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}

          {/* Common fields */}
          <div>
            <label className="text-xs font-medium block mb-1">Tên</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Vd: ERP Frontend Source"
              autoFocus
            />
          </div>

          <div>
            <label className="text-xs font-medium block mb-1">
              Slug
              <span className="ml-1 text-muted-foreground">
                (unique identifier, dùng cho agent reference)
              </span>
            </label>
            <Input
              value={slug}
              onChange={(e) => {
                setSlug(e.target.value);
                setSlugTouched(true);
              }}
              placeholder="erp-frontend"
              disabled={isEdit}
              className="font-mono text-sm"
            />
          </div>

          <div>
            <label className="text-xs font-medium block mb-1">
              Mô tả (optional)
            </label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="Mô tả ngắn về hệ thống này"
            />
          </div>

          {/* Type-specific config */}
          {type === "git" ? (
            <>
              <div>
                <label className="text-xs font-medium block mb-1">
                  Git URL
                </label>
                <Input
                  value={gitUrl}
                  onChange={(e) => setGitUrl(e.target.value)}
                  placeholder="https://github.com/owner/repo.git"
                  className="font-mono text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-medium block mb-1">
                  Branch (optional)
                </label>
                <Input
                  value={gitBranch}
                  onChange={(e) => setGitBranch(e.target.value)}
                  placeholder="main (default)"
                  className="text-sm"
                />
              </div>
            </>
          ) : null}

          {type === "url" ? (
            <div>
              <label className="text-xs font-medium block mb-1">URL</label>
              <Input
                value={urlValue}
                onChange={(e) => setUrlValue(e.target.value)}
                placeholder="https://docs.example.com/api"
                className="font-mono text-sm"
              />
            </div>
          ) : null}

          {type === "database" ? (
            <div>
              <label className="text-xs font-medium block mb-1">
                DSN (read-only credentials)
              </label>
              <Input
                value={dbDsn}
                onChange={(e) => setDbDsn(e.target.value)}
                placeholder="postgresql://user:pass@host:5432/dbname"
                className="font-mono text-sm"
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                ⚠ Khuyến nghị tạo user read-only riêng cho introspection
              </p>
            </div>
          ) : null}

          {/* Credentials secret ref */}
          {type !== "database" ? (
            <div>
              <label className="text-xs font-medium block mb-1">
                Credentials secret name (optional)
                <span className="ml-1 text-muted-foreground">
                  — Personal Access Token cho git, Bearer token cho URL
                </span>
              </label>
              <Input
                value={credentialsSecretName}
                onChange={(e) => setCredentialsSecretName(e.target.value)}
                placeholder="Vd: github_pat_internal"
                className="font-mono text-sm"
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                Cấu hình secret value trong Settings → API Keys (cần whitelist
                trước, hoặc chuyển vault hỗ trợ free-form sau v1).
              </p>
            </div>
          ) : null}
        </div>

        <footer className="flex justify-end gap-2 border-t px-4 py-3 sticky bottom-0 bg-popover">
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Huỷ
          </Button>
          <Button
            variant="brand"
            onClick={() => void handleSave()}
            disabled={busy}
          >
            {busy ? "Đang lưu..." : isEdit ? "Cập nhật" : "Thêm"}
          </Button>
        </footer>
      </div>
    </div>
  );
}
